/* eslint-disable max-lines */
import {
  BizNestClientError,
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IbuyerAnalytics,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile,
  IfilterProps,
  Isuccess
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Represents a buyer in the commerce system.
 * This class provides methods to manage buyer profiles, including CRUD operations,
 * file uploads, and profile management.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TcommerceUserProps - The type definition for buyer properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Buyer} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const buyer = await Buyer.getAll();
 *
 * const buyer = await Buyer.filterAll({
 *
 * });
 *
 * const buyer = await Buyer.bulkDelete({
 *
 * });
 *
 * const buyer = await Buyer.getOne('resourceId');
 *
 * const buyer = await Buyer.remove();
 *
 * ```
 */
export class Buyer extends DatabaseAuto {
  /** Base API endpoint for buyer operations */
  private static readonly baseUrl = '/buyer';

  /** Buyer's first name */
  readonly firstName: IcommerceUser['firstName'];

  /** Buyer's last name */
  readonly lastName: IcommerceUser['lastName'];

  /** Buyer's phone number */
  readonly phone: IcommerceUser['phone'];

  /** Buyer's email address */
  readonly email: IcommerceUser['email'];

  /** Reference to the authentication user */
  readonly authUser: IcommerceUser['authUser'];

  /** Unique reference ID for the buyer */
  readonly urId: IcommerceUser['urId'];

  /** Default currency for the buyer */
  readonly defaultCurrency: IcommerceUser['defaultCurrency'];

  /** Shipping addresses */
  readonly address?: IcommerceUser['address'];

  /** Billing information */
  readonly billing?: IcommerceUser['billing'];

  /** Buyer's date of birth */
  readonly dateOfBirth?: IcommerceUser['dateOfBirth'];

  /** Buyer's gender */
  readonly gender?: IcommerceUser['gender'];

  /** Amount due from the buyer */
  readonly amountDue?: IcommerceUser['amountDue'];

  /** Whether the buyer is currently online */
  readonly online?: IcommerceUser['online'];

  /** Salutation (e.g., Mr., Mrs.) */
  readonly salutation?: IcommerceUser['salutation'];

  /** Format for displaying the user's name */
  readonly userDispNameFormat?: IcommerceUser['userDispNameFormat'];

  /** Type of the user */
  readonly userType?: IcommerceUser['userType'];

  /** Array of photos associated with the buyer */
  readonly photos?: IcommerceUser['photos'];

  /** URL to the buyer's profile picture */
  readonly profilePicture?: IcommerceUser['profilePicture'];

  /** URL to the buyer's profile cover picture */
  readonly profileCoverPicture?: IcommerceUser['profileCoverPicture'];

  /** Recovery phone numbers for account recovery */
  readonly recoveryPhoneNumbers?: IcommerceUser['recoveryPhoneNumbers'];

  /** Recovery emails for account recovery */
  readonly recoveryEmails?: IcommerceUser['recoveryEmails'];

  /** Authentication photos for verification */
  readonly authPhotos: IcommerceUser['authPhotos'];

  /**
   * Creates a new instance of Buyer.
   * @param {IcommerceUser} data - The buyer data
   * @throws {BizNestClientError} If no data is provided
   */
  constructor(data: IcommerceUser) {
    if (!data) {
      throw new BizNestClientError('No data provided');
    }
    super(data);
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.phone = data.phone || '';
    this.email = data.email || '';
    this.authUser = data.authUser as string || '';
    this.urId = data.urId || '';
    this.defaultCurrency = data.defaultCurrency || '';
    this.address = data.address || [];
    this.billing = data.billing || [];
    this.dateOfBirth = data.dateOfBirth || '';
    this.gender = data.gender || '';
    this.amountDue = data.amountDue || 0;
    this.online = data.online || false;
    this.salutation = data.salutation || '';
    this.userDispNameFormat = data.userDispNameFormat;
    this.userType = data.userType;
    this.photos = data.photos || [];
    this.profilePicture = data.profilePicture;
    this.profileCoverPicture = data.profileCoverPicture;
    this.recoveryPhoneNumbers = data.recoveryPhoneNumbers || [];
    this.recoveryEmails = data.recoveryEmails || [];
    this.authPhotos = data.authPhotos;
  }

  /**
   * Retrieves analytics data for buyers.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IbuyerAnalytics>} The analytics data
   * @throws {Error} If there's an error fetching the analytics
   */
  static analytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbuyerAnalytics>(
        `${Buyer.baseUrl}/analytics`,
        filter
      );

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all buyers with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Buyer[] }>}
   * An object containing the total count and an array of buyers
   * @throws {Error} If there's an error fetching the buyers
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IcommerceUser>>(`${Buyer.baseUrl}?${query}`);

    const buyers = await lastValueFrom(observer$);

    return {
      count: buyers.count,
      dataArr: buyers.data.map(val => new Buyer(val))
    };
  }

  /**
   * Retrieves buyers based on filter criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Buyer[] }>}
   * An object containing the total count and an array of filtered buyers
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IcommerceUser>>(
        `${Buyer.baseUrl}/filter`,
        filter
      );

    const buyers = await lastValueFrom(observer$);

    return {
      count: buyers.count,
      dataArr: buyers.data.map(val => new Buyer(val))
    };
  }

  /**
   * Retrieves a single buyer by ID.
   * @param {string} resourceId - The unique identifier of the buyer
   * @returns {Promise<Buyer>} The requested buyer
   * @throws {Error} If the buyer is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IcommerceUser>(`${Buyer.baseUrl}/${resourceId}`);

    const buyer = await lastValueFrom(observer$);

    return new Buyer(buyer);
  }

  /**
   * Retrieves the current authenticated buyer's profile.
   * @returns {Promise<Buyer>} The current buyer's profile
   * @throws {Error} If the buyer is not authenticated or an error occurs
   */
  static async myProfile() {
    const observer$ = CommerceClient.ehttp
      .makePost<IcommerceUser>(
        `${Buyer.baseUrl}/my-profile`,
        {}
      );

    const buyer = await lastValueFrom(observer$);

    return new Buyer(buyer);
  }

  /**
   * Creates a new buyer.
   * @param {Omit<IcommerceUser, 'userType' | '_id' | 'createdAt' | 'updatedAt' | 'authUser'>} payload - The buyer data
   * @returns {Promise<Buyer>} The newly created buyer
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: Omit<IcommerceUser, 'userType' | '_id' | 'createdAt' | 'updatedAt' | 'authUser' | 'urId'>) {
    const observer$ = CommerceClient.ehttp
      .makePost<IcommerceUser>(
        `${Buyer.baseUrl}`,
        payload
      );

    return new Buyer(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple buyers based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>}
   * The result of the bulk deletion operation
   * @throws {Error} If the bulk deletion fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${Buyer.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Uploads multiple images for the current buyer.
   * @param {Ifile[]} files - The files to upload
   * @returns {Promise<IcommerceUser>} The updated buyer with new images
   * @throws {Error} If the upload fails or the buyer doesn't exist
   */
  uploadImages(files: Ifile[]) {
    return CommerceClient.ehttp.uploadFiles<IcommerceUser>(
      files,
      `${Buyer.baseUrl}/upload-images`,
      { _id: this._id }
    );
  }

  /**
   * Uploads a profile picture for the current buyer.
   * @param {Ifile} file - The image file to upload
   * @returns {Promise<IcommerceUser>} The updated buyer with new profile picture
   * @throws {Error} If the upload fails or the buyer doesn't exist
   */
  uploadProfilePicture(file: Ifile) {
    return CommerceClient.ehttp.uploadFiles<IcommerceUser>(
      [file],
      `${Buyer.baseUrl}/upload-profile-picture`,
      { _id: this._id }
    );
  }

  /**
   * Uploads a profile cover picture for the current buyer.
   * @param {Ifile} file - The image file to upload
   * @returns {Promise<IcommerceUser>} The updated buyer with new cover picture
   * @throws {Error} If the upload fails or the buyer doesn't exist
   */
  uploadProfileCoverPicture(file: Ifile) {
    return CommerceClient.ehttp.uploadFiles<IcommerceUser>(
      [file],
      `${Buyer.baseUrl}/upload-profile-cover-picture`,
      { _id: this._id }
    );
  }

  /**
   * Sets an existing image as profile or cover picture.
   * @param {string} fileId - The ID of the file to set
   * @param {'profilePicture' | 'profileCoverPicture'} field - The field to set the image for
   * @returns {Promise<Buyer>} The updated buyer
   * @throws {Error} If the operation fails or the buyer/image doesn't exist
   */
  async setImageAs(
    fileId: string,
    field: 'profilePicture' | 'profileCoverPicture'
  ) {
    const observer$ = CommerceClient.ehttp
      .makePost<IcommerceUser>(
        `${Buyer.baseUrl}/set-image-as`,
        { fileId, field }
      );

    return new Buyer(await lastValueFrom(observer$));
  }

  /**
   * Deletes images from the current buyer's profile.
   * @param {string[]} fileIds - The IDs of the images to delete
   * @returns {Promise<Buyer>} The updated buyer with images removed
   * @throws {Error} If the deletion fails or the buyer doesn't exist
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = CommerceClient.ehttp
      .makePost<IcommerceUser>(
        `${Buyer.baseUrl}/delete-images`,
        { fileIds, _id: this._id }
      );

    return new Buyer(await lastValueFrom(observer$));
  }

  /**
   * Updates the current buyer's information.
   * @param {Omit<Partial<IcommerceUser>, 'userType' | '_id' |
   * 'createdAt' | 'updatedAt'>} payload - The updated buyer data
   * @returns {Promise<Buyer>} The updated buyer
   * @throws {Error} If the update fails or the buyer doesn't exist
   */
  async update(payload: Omit<Partial<IcommerceUser>, 'userType' | '_id' | 'createdAt' | 'updatedAt'>) {
    const observer$ = CommerceClient.ehttp
      .makePut<IcommerceUser>(
        `${Buyer.baseUrl}/${this._id}`,
        payload
      );

    return new Buyer(await lastValueFrom(observer$));
  }

  /**
   * Permanently deletes the current buyer.
   * @returns {Promise<IdeleteOneResponse | null>}
   * The result of the deletion operation
   * @throws {Error} If the deletion fails or the buyer doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Buyer.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }

  /**
   * Changes the default currency for the current buyer.
   * @param {string} currency - The new currency code
   * @returns {Promise<Isuccess>} The result of the currency change operation
   * @throws {Error} If the currency change fails or the buyer doesn't exist
   */
  changeUserCurrency(currency: string) {
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(
        `${Buyer.baseUrl}/change-currency`,
        { currency }
      );

    return lastValueFrom(observer$);
  }
}
